import axios from 'axios';
import Cookies from 'js-cookie';
import i18n from 'i18next';
import { createIntl } from '@formatjs/intl';
import { timeZoneCookie, isBrowserTimeZoneCookie } from '../constants';
import { buildIntlConfig, setIntl } from '../intl';
import { timeZoneAliases } from './timeZoneAliases';

function getCookieDomain(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return undefined;
  const parts = hostname.split('.');
  if (parts.length < 2) return undefined;
  return '.' + parts.slice(-2).join('.');
}

function getBrowserTimeZone(): string {
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return timeZoneAliases[browserTz] || browserTz;
}

function setTimeZoneCookies(timeZone: string, isBrowserTz: boolean): void {
  const domain = getCookieDomain();
  const options: Cookies.CookieAttributes = {
    sameSite: 'lax',
    ...(domain && { domain })
  };
  Cookies.set(timeZoneCookie, timeZone, options);
  Cookies.set(isBrowserTimeZoneCookie, String(isBrowserTz), options);
}

function refreshIntlTimeZone(): void {
  const locale = i18n.language || 'en-US';
  setIntl(createIntl(buildIntlConfig(locale)));
}

/**
 * Synchronises the browser's detected timezone with the identity server and local cookies.
 *
 * Logic:
 * - If no timezone cookie exists → save browser TZ to DB with isBrowserTimeZone=true.
 * - If isBrowserTimeZone cookie is "true" AND the browser TZ differs from the cookie TZ
 *   → update the DB and cookies with the new browser TZ.
 * - If isBrowserTimeZone is "false" (user has a preferred TZ) → no action.
 *
 * After updating, the formatjs `intl` instance is refreshed so date rendering picks up the change.
 */
export async function syncBrowserTimeZone(userId: string): Promise<void> {
  const browserTz = getBrowserTimeZone();
  const currentCookieTz = Cookies.get(timeZoneCookie);
  const isBrowserTzCookie = Cookies.get(isBrowserTimeZoneCookie);

  // Case 1: No timezone in cookies at all (first login or cookies were deleted)
  // Case 2: isBrowserTimeZone is true and browser TZ differs from cookie → update
  if (!currentCookieTz || !isBrowserTzCookie || (isBrowserTzCookie === 'true' && currentCookieTz !== browserTz)) {
    try {
      const timeZoneUrl = `/identity/api/v2/timeZones/${userId}`;
      const { data } = await axios.get(timeZoneUrl);
      let targetTz = browserTz;
      let targetIsBrowserTz = true;

      if (data.isBrowserTimeZone === false || (data.isBrowserTimeZone === true && data.timeZone === browserTz)) {
        // Backend either specifies a custom user preferred timezone,
        // or the backend is already in sync with the current browser timezone.
        // We only need to populate the cookies locally.
        targetTz = data.timeZone;
        targetIsBrowserTz = Boolean(data.isBrowserTimeZone);
      } else {
        // Backend is either missing timezone data entirely,
        // or is out of sync with the current browser timezone. Update the backend.
        await axios.put(timeZoneUrl, {
          userId,
          timeZone: browserTz,
          isBrowserTimeZone: true
        });
      }

      setTimeZoneCookies(targetTz, targetIsBrowserTz);
      refreshIntlTimeZone();
    } catch (e) {
      console.error('Failed to sync browser timezone:', e);
    }
    return;
  }
}
